import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import profileIcon from "../../../assets/profile_icon.svg";
import { WorkOrderApi } from "../../../api/workorders/service";
import { convertToStringIndex } from "./ContractorDetails";
import UpdateContractSidebar from "./UpdateContract";
import { useAlert } from "components/UI_components/Alert";

const UTC_to_LocalDate = (utcDateStr) => {
  if (!utcDateStr) return "";
  const date = new Date(utcDateStr);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const IndividualWorkOrder = ({ contractor, workOrder, onClose }) => {
  const workOrderId = workOrder?.id;
  const { addAlert } = useAlert();
  const [details, setDetails] = useState(workOrder || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [showUpdateSidebar, setShowUpdateSidebar] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    let active = true;
    async function load() {
      if (!workOrderId) return;
      setLoading(true);
      setError("");
      setHistoryLoading(true);
      setHistoryError("");

      try {
        const [detailsData, historyData] = await Promise.all([
          WorkOrderApi.getById(workOrderId),
          WorkOrderApi.getContractHistory(workOrderId),
        ]);

        if (active) {
          setDetails(detailsData);
          const rows = Array.isArray(historyData)
            ? historyData
            : Array.isArray(historyData?.data)
            ? historyData.data
            : historyData?.results || [];
          setHistory(rows);
        }
      } catch (e) {
        if (active) {
          setError(e?.message || "Failed to load work order");
          setHistoryError(e?.message || "Failed to load history");
        }
      } finally {
        if (active) {
          setLoading(false);
          setHistoryLoading(false);
        }
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [workOrderId]);

  const issueDate =
    details?.date_of_issue || new Date().toISOString().split("T")[0];
  const projectName = details?.site_name || "Project unassigned";
  const totalAmount = Number(details?.total_amount || 0);
  const totalPaidAmount = Number(details?.total_paid_amount || 0);

  const startDate = details?.start_date || "";
  const endDate = details?.end_date || "";
  const durationDays = useMemo(() => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const ms = end.getTime() - start.getTime();
    if (Number.isNaN(ms)) return null;
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  }, [startDate, endDate]);

  // Get latest and previous contracts
  const latestContract = history.length > 0 ? history[0] : null;
  const previousContracts = history.length > 1 ? history.slice(1) : [];

  const toggleExpanded = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleUpdateContract = () => {
    setShowUpdateSidebar(true);
  };

  return (
    <div className="flex flex-col p-2 w-[84%] h-[92vh] overflow-y-scroll mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <button onClick={onClose} className="text-[#232323] hover:text-black">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <p className="ml-[30px] text-[#232323] text-[20px] font-medium">
            #WO_{workOrder?.site}_{convertToStringIndex(workOrder?.id)}
          </p>
        </div>
        <button
          onClick={handleUpdateContract}
          className="bg-[#3B0069] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#2d0051]"
        >
          Upgrade Contract
        </button>
      </div>

      {/* Top Card - Basic Info */}
      <div className="bg-white border shadow-md rounded-xl p-4 shrink-0">
        <div className="flex items-center gap-3 mb-4">
          <img src={profileIcon} alt="Contractor" className="w-10 h-10" />
          <div>
            <p className="font-medium text-[16px] text-[#232323]">
              {contractor?.name || "Contractor"}{" "}
              <span className="text-xs bg-[#E3E3E3] text-[#737373] px-2 py-1 rounded-full ml-2">
                Contractor
              </span>
            </p>
            <p className="text-[14px] font-light text-[#232323]">
              {contractor?.contractor_type || ""}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <label className="text-[15px] text-[#232323] font-md block mb-1">
              Date of Issue
            </label>
            <input
              type="text"
              className="bg-[#F3F4F6] h-[48px] p-2 rounded-lg w-full text-sm text-[#232323]"
              value={issueDate}
              readOnly
            />
          </div>
          <div>
            <label className="text-[15px] text-[#232323] font-md block mb-1">
              Project
            </label>
            <div className="relative">
              <div className="flex bg-[#F3F4F6] h-[40px] items-center justify-between p-2 rounded-lg">
                <span className="text-[15px] font-medium text-[#737373]">
                  {projectName}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <label className="text-[15px] text-[#232323] font-md block mb-1">
              Total Amount
            </label>
            <input
              type="text"
              className="bg-[#F3F4F6] h-[48px] p-2 rounded-lg w-full text-sm text-[#232323]"
              value={`₹${totalAmount.toLocaleString()}`}
              readOnly
            />
          </div>
          <div>
            <label className="text-[15px] text-[#232323] font-md block mb-1">
              Total Paid
            </label>
            <input
              type="text"
              className="bg-[#F3F4F6] h-[48px] p-2 rounded-lg w-full text-sm text-[#232323]"
              value={`₹${totalPaidAmount.toLocaleString()}`}
              readOnly
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 items-start mb-2">
          <div>
            <label className="text-[15px] text-[#232323] font-md block mb-1">
              Start & End Date
            </label>
            <div className="bg-[#F3F4F6] h-[48px] w-full rounded-lg px-3 py-2 flex items-center justify-between text-[#232323]">
              <span className="text-sm">{startDate || "—"}</span>
              <span className="text-sm mx-2">to</span>
              <span className="text-sm">{endDate || "—"}</span>
            </div>
          </div>
          <div className="text-[16px] font-medium text-[#3B0069] bg-[#ECE6F0] px-3 py-1 rounded-full h-[30px] w-fit mt-10">
            Contract Duration:{" "}
            {durationDays == null ? "—" : `${durationDays} Days`}
          </div>
        </div>
        {error ? (
          <div className="text-red-600 text-sm mt-2">{error}</div>
        ) : null}
      </div>

      {/* Latest Contract Details */}
      {historyLoading ? (
        <div className="bg-white mt-2 shadow-md rounded-xl p-4">
          <div className="text-sm text-[#737373]">
            Loading contract details...
          </div>
        </div>
      ) : latestContract ? (
        <div className="mt-2 pr-1">
          <div className="bg-white mt-2 shadow-md rounded-xl p-4 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[16px] font-semibold text-[#232323]">
                Current Contract
              </h3>
              <div className="flex items-center gap-4 text-xs text-[#737373]">
                <span>
                  Updated: {UTC_to_LocalDate(latestContract.updated_on) || "—"}
                </span>
                <span>
                  {previousContracts.length > 0 ? "Extended by:" : "End Date:"}{" "}
                  {UTC_to_LocalDate(latestContract.end_date) || "—"}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[15px] text-[#232323] font-md block mb-1">
                  Contract Amount
                </label>
                <input
                  type="text"
                  className="bg-[#F3F4F6] h-[48px] p-2 rounded-lg w-full text-sm text-[#232323]"
                  value={`₹${Number(
                    latestContract.contract_amount || 0
                  ).toLocaleString()}`}
                  readOnly
                />
              </div>
            </div>

            <div>
              <label className="text-[15px] font-md text-[#232323] block mb-1">
                Scope of Work
              </label>
              <textarea
                placeholder="—"
                className="w-full bg-[#F3F4F6] p-3 rounded-lg text-[15px] font-medium text-[#737373] resize-none h-[75px]"
                value={latestContract.scope_of_work || ""}
                readOnly
              />
            </div>

            <div>
              <label className="text-[15px] font-md text-[#232323] block mb-1">
                Payment terms
              </label>
              <textarea
                placeholder="—"
                className="w-full bg-[#F3F4F6] p-3 rounded-lg text-[15px] font-medium text-[#737373] resize-none h-[75px]"
                value={latestContract.payment_terms || ""}
                readOnly
              />
            </div>

            <div>
              <label className="text-[15px] font-md text-[#232323] block mb-1">
                Terms & Conditions
              </label>
              <textarea
                placeholder="—"
                className="w-full bg-[#F3F4F6] p-3 rounded-lg text-[15px] font-medium text-[#737373] resize-none h-[75px]"
                value={latestContract.terms_and_conditions || ""}
                readOnly
              />
            </div>

            <div>
              <label className="text-[15px] font-md text-[#232323] block mb-1">
                Approval & Acknowledgement
              </label>
              <textarea
                placeholder="—"
                className="w-full bg-[#F3F4F6] p-3 rounded-lg text-[15px] font-medium text-[#737373] resize-none h-[75px]"
                value={latestContract.approval_and_acknowledgement || ""}
                readOnly
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* Previous Contracts */}
      {previousContracts.length > 0 && (
        <div className="mt-2 pr-1">
          <div className="bg-white mt-2 shadow-md rounded-xl p-4">
            <h3 className="text-[16px] font-semibold text-[#232323] mb-3">
              Previous Contracts
            </h3>
            <div className="space-y-2">
              {previousContracts.map((contract, idx) => (
                <div key={idx} className="border rounded-lg bg-[#F9FAFB]">
                  <button
                    onClick={() => toggleExpanded(idx)}
                    className="w-full flex items-center justify-between p-3 hover:bg-[#F3F4F6] rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium text-[#232323]">
                        End Date: {contract.end_date || "—"}
                      </span>
                      <span className="text-xs text-[#737373]">
                        Updated: {UTC_to_LocalDate(contract.updated_on) || "—"}
                      </span>
                      <span className="text-sm font-medium text-[#3B0069]">
                        ₹
                        {Number(contract.contract_amount || 0).toLocaleString()}
                      </span>
                    </div>
                    {expandedItems[idx] ? (
                      <ChevronUp className="w-4 h-4 text-[#737373]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#737373]" />
                    )}
                  </button>

                  {expandedItems[idx] && (
                    <div className="px-3 pb-3 space-y-3 border-t pt-3">
                      <div>
                        <div className="text-xs text-[#737373] mb-1">
                          Scope of Work
                        </div>
                        <div className="text-sm text-[#232323] bg-white p-2 rounded">
                          {contract.scope_of_work || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-[#737373] mb-1">
                          Payment Terms
                        </div>
                        <div className="text-sm text-[#232323] bg-white p-2 rounded">
                          {contract.payment_terms || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-[#737373] mb-1">
                          Terms & Conditions
                        </div>
                        <div className="text-sm text-[#232323] bg-white p-2 rounded">
                          {contract.terms_and_conditions || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-[#737373] mb-1">
                          Approval & Acknowledgement
                        </div>
                        <div className="text-sm text-[#232323] bg-white p-2 rounded">
                          {contract.approval_and_acknowledgement || "—"}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {historyError && (
        <div className="mt-2 pr-1">
          <div className="bg-white mt-2 shadow-md rounded-xl p-4">
            <div className="text-sm text-red-600">{historyError}</div>
          </div>
        </div>
      )}
      <UpdateContractSidebar
        isOpen={showUpdateSidebar}
        onClose={() => {
          setShowUpdateSidebar(false);
        }}
        workOrderId={workOrderId}
        currentContract={latestContract}
        onUpdateSuccess={() => {
          addAlert("Contract updated successfully", "success");
        }}
      />
    </div>
  );
};

export default IndividualWorkOrder;
